import React, { useState, useRef, useEffect } from 'react';
import { X, Copy, Download, CheckCircle, Maximize2, Minimize2 } from 'lucide-react';
import { copyToClipboard, downloadFile } from '../../utils/helpers';

/**
 * Modal component for viewing and interacting with XML content
 * Features:
 * - Syntax highlighting
 * - Copy to clipboard
 * - Download as file
 * - Fullscreen toggle
 * - Keyboard shortcuts (Escape to close)
 */
const XmlViewerModal = ({ isOpen, onClose, xmlContent, fileName = 'message.xml', title = 'XML Message' }) => {
  const [copied, setCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const modalRef = useRef(null);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle click outside modal to close
  const handleBackdropClick = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
  };

  const handleCopy = async () => {
    const success = await copyToClipboard(xmlContent);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    downloadFile(xmlContent, fileName, 'application/xml');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="xml-viewer-title"
    >
      <div
        ref={modalRef}
        className={`bg-white rounded-lg shadow-2xl flex flex-col transition-all ${
          isFullscreen
            ? 'w-[95vw] h-[95vh]'
            : 'w-full max-w-4xl h-[80vh] mx-4'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <h2 id="xml-viewer-title" className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <span className="text-blue-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
            </span>
            <span>{title}</span>
          </h2>

          <div className="flex items-center space-x-2">
            {/* Copy Button */}
            <button
              onClick={handleCopy}
              className="btn-secondary flex items-center space-x-2 text-sm"
              title="Copy to clipboard (Ctrl/Cmd + C)"
            >
              {copied ? (
                <>
                  <CheckCircle className="w-4 h-4 text-success-600" />
                  <span className="text-success-600">Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span>Copy</span>
                </>
              )}
            </button>

            {/* Download Button */}
            <button
              onClick={handleDownload}
              className="btn-secondary flex items-center space-x-2 text-sm"
              title="Download XML file"
            >
              <Download className="w-4 h-4" />
              <span>Download</span>
            </button>

            {/* Fullscreen Toggle */}
            <button
              onClick={toggleFullscreen}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? (
                <Minimize2 className="w-5 h-5" />
              ) : (
                <Maximize2 className="w-5 h-5" />
              )}
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
              title="Close (Escape)"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden p-6">
          <div className="h-full overflow-auto bg-gray-900 rounded-lg">
            <pre className="p-4 text-sm font-mono leading-relaxed">
              <code className="language-xml text-gray-100">
                {formatXml(xmlContent)}
              </code>
            </pre>
          </div>
        </div>

        {/* Footer with file info */}
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              Size: {new Blob([xmlContent]).size.toLocaleString()} bytes
            </span>
            <span>
              Lines: {xmlContent.split('\n').length.toLocaleString()}
            </span>
            <span className="text-xs text-gray-500">
              Press <kbd className="px-2 py-1 bg-gray-200 rounded">Esc</kbd> to close
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Robust XML formatter that properly handles indentation
 * Keeps simple text content inline: <tag>text</tag>
 * Formats nested elements on separate lines with proper indentation
 */
const formatXml = (xml) => {
  if (!xml) return '';
  
  // If already well-formatted (has proper indentation), return as-is
  if (xml.match(/\n\s{2,}/)) {
    return xml;
  }

  try {
    let formatted = '';
    let indent = 0;
    const tab = '  ';
    
    // Normalize the XML by removing extra whitespace between tags
    const normalized = xml.replace(/>\s+</g, '><').replace(/\s+/g, ' ');
    
    // Split into individual tags while preserving the brackets
    const tokens = normalized.match(/<[^>]+>|[^<]+/g) || [];
    
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];
      const trimmed = token.trim();
      
      // Skip empty tokens
      if (!trimmed) continue;
      
      // Check if this is a tag or text content
      const isTag = token.startsWith('<');
      
      if (isTag) {
        const isClosing = token.startsWith('</');
        const isSelfClosing = token.endsWith('/>');
        const isDeclaration = token.startsWith('<?') || token.startsWith('<!');
        
        // Look ahead to see what's next
        const nextToken = i + 1 < tokens.length ? tokens[i + 1] : null;
        const hasTextContent = nextToken && !nextToken.startsWith('<');
        
        // Decrease indent for closing tags (before printing)
        if (isClosing) {
          indent = Math.max(0, indent - 1);
          
          // Only add newline for closing tags if NOT preceded by text content
          const prevToken = i - 1 >= 0 ? tokens[i - 1] : null;
          const afterText = prevToken && !prevToken.startsWith('<');
          
          if (!afterText && formatted) {
            formatted += '\n' + tab.repeat(indent);
          }
        } else if (formatted) {
          // Add newline for opening tags (unless after opening tag with text)
          formatted += '\n' + tab.repeat(indent);
        }
        
        // Add the tag
        formatted += token;
        
        // Increase indent for opening tags (after printing)
        if (!isClosing && !isSelfClosing && !isDeclaration) {
          indent++;
        }
      } else {
        // Text content - add inline immediately after opening tag
        formatted += trimmed;
      }
    }
    
    return formatted;
  } catch (error) {
    console.error('Error formatting XML:', error);
    return xml; // Return original if formatting fails
  }
};

export default XmlViewerModal;
